import { generateHTML } from '@tiptap/html';
import { isEmpty } from 'lodash';

import { SINGLE_EDITOR_TASK_LIST_STATIC_TIPTAP_EXTENSIONS } from '../tiptap/extensions/taskList/taskListExtensionLists';
import { TiptapContent, TiptapContentNode, TiptapNodeType } from '../tiptap/tiptapTypes';

// NOTE: Like `taskListClipboardHtmlUtils`, and unlike the rest of `src/common/taskLists/`, this
//  module is not server-safe - it renders through an extension list that pulls in React and SCSS.

// The indent the plain text rendering adds for each level of sub-task nesting
const TEXT_INDENT = '    ';

/**
 * Lifts each task item's text out of the block wrappers the schema renders it in.
 *
 * A task item renders as `<li><label>…</label><div><p>text</p></div></li>`. Those two block
 * elements put the text on its own line in every other application, so a pasted list arrives
 * double spaced. Unwrapping them leaves the ordinary `<li>text<ul>…</ul></li>` shape, which pastes
 * as a plain list, and which Tiptap still parses straight back into a task item's paragraph.
 */
const inlineTaskItemText = (html: string): string => {
    const container = document.createElement('div');
    container.innerHTML = html;

    // Document order, so an item's own wrapper is always unwrapped before its sub-tasks' are. Each
    // sub-task stays in the tree as its wrapper is lifted, so its own entry is still unwrappable.
    container.querySelectorAll('li[data-type="taskItem"] > div').forEach((contentWrapper) => {
        const taskTextParagraph = contentWrapper.firstElementChild;

        if (taskTextParagraph?.tagName === 'P') taskTextParagraph.replaceWith(...taskTextParagraph.childNodes);

        contentWrapper.replaceWith(...contentWrapper.childNodes);
    });

    return container.innerHTML;
};

/**
 * Renders a single-editor task list's document as the clipboard HTML for a copy of its element.
 *
 * Rendered through the same schema that reads a task list back off the clipboard, so a copy
 * carries the title tag and the private chip attrs, and pastes as a task list rather than as a
 * plain checklist.
 */
export const createTaskListClipboardHtml = (textContent: TiptapContent): string =>
    inlineTaskItemText(generateHTML(textContent, SINGLE_EDITOR_TASK_LIST_STATIC_TIPTAP_EXTENSIONS));

/**
 * Reads a node's own text, joining the inline content directly beneath it.
 *
 * Deliberately shallow: only a task's paragraph and the list's title are read this way, and a
 * task's nested sub-task list is rendered as its own lines.
 */
const getNodeText = (node?: TiptapContentNode): string =>
    (node?.content ?? [])
        .map((child) => {
            if (child.type === TiptapNodeType.inlineBoardLink) return child.attrs?.title ?? '';
            return child.text ?? '';
        })
        .join('');

/**
 * Collects the plain text lines for a task list node's items, indenting each level of sub-tasks.
 */
const collectTaskItemLines = (taskList: TiptapContentNode, depth: number): string[] =>
    (taskList.content ?? []).flatMap((taskItem) => {
        const taskItemContent = taskItem.content ?? [];
        const taskText = getNodeText(taskItemContent.find((node) => node.type === TiptapNodeType.paragraph));
        const subTaskLists = taskItemContent.filter((node) => node.type === TiptapNodeType.taskList);

        return [
            `${TEXT_INDENT.repeat(depth)}${taskText}`,
            ...subTaskLists.flatMap((subTaskList) => collectTaskItemLines(subTaskList, depth + 1)),
        ];
    });

/**
 * Renders a single-editor task list's document as the clipboard plain text for a copy of its
 * element: the title, then one line per task with sub-tasks indented.
 *
 * A V2 list holds its title as a document node rather than as an element property, so it is read
 * from the same document as the tasks. A title that is shown but never typed into is skipped,
 * rather than copied as a blank leading line.
 */
export const createTaskListClipboardText = (textContent: TiptapContent): string => {
    const lines = (textContent.content ?? []).flatMap((node) => {
        if (node.type === TiptapNodeType.taskListTitle) {
            const title = getNodeText(node);
            return isEmpty(title) ? [] : [title];
        }

        return node.type === TiptapNodeType.taskList ? collectTaskItemLines(node, 0) : [];
    });

    return lines.join('\n');
};
